import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

interface Checklist {
  id: string;
  title: string;
  sprintId: string;
  checklistType: "manual" | "automated";
  description: string | null;
}

interface ChecklistFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  checklist?: Checklist | null;
}

export function ChecklistFormDialog({ open, onClose, onSuccess, checklist }: ChecklistFormDialogProps) {
  const { toast } = useToast();
  const isEditing = !!checklist;

  const [formData, setFormData] = useState({
    title: "",
    sprint_id: "",
    checklist_type: "manual" as "manual" | "automated",
    description: "",
  });

  // Load sprints for dropdown
  const { data: sprints = [] } = useQuery<Array<{ id: string; sprintNumber: string; sprintName: string }>>({
    queryKey: ["/api/project-manager/sprints"],
    enabled: open,
  });

  // Reset form when dialog opens/closes or checklist changes
  useEffect(() => {
    if (open) {
      if (checklist) {
        setFormData({
          title: checklist.title,
          sprint_id: checklist.sprintId,
          checklist_type: checklist.checklistType,
          description: checklist.description || "",
        });
      } else {
        setFormData({
          title: "",
          sprint_id: "",
          checklist_type: "manual",
          description: "",
        });
      }
    }
  }, [open, checklist]);

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await apiRequest("POST", "/api/testing/checklists", data);
      return res.json();
    },
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await apiRequest("PATCH", `/api/testing/checklists/${checklist?.id}`, data);
      return res.json();
    },
    onSuccess: () => {
      onSuccess();
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.sprint_id) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Title and Sprint are required.",
      });
      return;
    }

    if (isEditing) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px]" data-testid="checklist-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Testing Checklist" : "Create Testing Checklist"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the testing checklist details."
              : "Create a new manual or automated testing checklist for a sprint."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title"
              placeholder="SPRINT-1 Manual Testing Checklist"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              data-testid="checklist-title-input"
            />
          </div>

          {/* Sprint */}
          <div className="space-y-2">
            <Label htmlFor="sprint_id">
              Sprint <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.sprint_id}
              onValueChange={(value) => setFormData({ ...formData, sprint_id: value })}
            >
              <SelectTrigger id="sprint_id" data-testid="sprint-select">
                <SelectValue placeholder="Select a sprint" />
              </SelectTrigger>
              <SelectContent>
                {sprints.map((sprint) => (
                  <SelectItem key={sprint.id} value={sprint.sprintNumber}>
                    {sprint.sprintNumber}{sprint.sprintName ? ` - ${sprint.sprintName}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Checklist Type */}
          <div className="space-y-2">
            <Label htmlFor="checklist_type">Checklist Type</Label>
            <Select
              value={formData.checklist_type}
              onValueChange={(value) => setFormData({ ...formData, checklist_type: value as "manual" | "automated" })}
            >
              <SelectTrigger id="checklist_type" data-testid="type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="automated">Automated</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Checklist purpose and scope..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              data-testid="description-textarea"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} data-testid="submit-checklist-button">
              {isLoading ? "Saving..." : isEditing ? "Update Checklist" : "Create Checklist"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
